import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import SummaryInfoCard from "../../../../../components/app-specific/summary-cards/summary-info-card";
import {useAppSelector} from "../../../../../redux";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {formatMoney} from "../../../../../../core/services/utils/utils";

const bodyIds = {
    future: 'future',
    current: 'current',
    period0: 'period0',
    period1: 'period1',
    period2: 'period2',
    period3: 'period3',
    total: 'total',
};

/**
 * Middle top-section SummaryInfoCard: vendor AP account summary (API).
 */
const PurchaseVendorInfoCard = ({vendorId, loading: outerLoading = false}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.vendorInfo'});
    const vendor = useAppSelector((state) => state.vendorInformation);
    const isMounted = useIsMounted();
    const [accountLoading, setAccountLoading] = useState(true);
    const [accountSummary, setAccountSummary] = useState(null);

    /**
     * Loads vendor AP account summary from the server.
     */
    const loadAccountSummary = useCallback(async () => {
        if (!vendorId || Number.isNaN(Number(vendorId))) {
            setAccountSummary(null);
            setAccountLoading(false);
            return;
        }
        setAccountLoading(true);
        const response = await Api.getVendorAccountSummary(Number(vendorId));
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setAccountSummary(response.data ?? null);
        } else {
            setAccountSummary(null);
        }
        setAccountLoading(false);
    }, [isMounted, vendorId]);

    useEffect(() => {
        void loadAccountSummary();
    }, [loadAccountSummary]);

    const loading = outerLoading || accountLoading;

    const moneyRow = useCallback((id, title, value) => ({
        id,
        title,
        value: value ?? 0,
        format: formatMoney,
    }), []);

    const summary = useMemo(() => {
        const data = accountSummary ?? {};
        return {
            header: {
                title: t('headerTitle'),
                value: vendor?.name ?? t('headerFallback'),
                loadingValue: t('loading'),
            },
            body: [
                moneyRow(bodyIds.future, t('future'), data.future),
                moneyRow(bodyIds.current, t('current'), data.current),
                moneyRow(bodyIds.period0, t('period0'), data.period0),
                moneyRow(bodyIds.period1, t('period1'), data.period1),
                moneyRow(bodyIds.period2, t('period2'), data.period2),
                moneyRow(bodyIds.period3, t('period3'), data.period3),
                moneyRow(bodyIds.total, t('total'), data.totalBalance ?? data.total),
            ],
        };
    }, [accountSummary, moneyRow, t, vendor?.name]);

    return (
        <SummaryInfoCard
            header={summary.header}
            body={summary.body}
            loading={loading}
            loadingCount={summary.body.length}
        />
    );
};

export default PurchaseVendorInfoCard;
